import React from 'react';
import { useVehicleInspection } from '@hooks/useVehicleInspection';
import { AppBanner } from './AppBanner/AppBanner';
import { UrgencyBadge } from './UrgencyBadge/UrgencyBadge';
import { SearchForm } from './SearchForm/SearchForm';
import { LookupResult } from './LookupResult/LookupResult';
import { ContextPanel } from './ContextPanel/ContextPanel';
import { VehicleInspectionBlockProps } from './VehicleInspection.types';
import {
  asVehicleInspectionContextKey,
  createDate,
  getVehicleInspectionContextKey,
  getUrgencyLevel,
  getDaysUntilDate,
  isGUTestEligible,
} from './VehicleInspection.utils';
import { getAppBannerContent, shouldShowAppBanner } from './VehicleInspection.content';
import { InfoText } from './InfoText/InfoText';
import * as S from './VehicleInspection.styles';

export const VehicleInspectionBlock = ({ contexts = [] }: VehicleInspectionBlockProps) => {
  const { vehicleInspection, lookup, isLoading: isLoadingSearch, error: errorSearch } = useVehicleInspection();
  const { manufactureName, modelName, firstRegisteredDate, nextInspectionDate, contextKey } = vehicleInspection || {};
  const vehicleBadgeText = [manufactureName, modelName].filter(Boolean).join(' ');
  const isGuEligible = isGUTestEligible(firstRegisteredDate);
  const selectedContextKey =
    asVehicleInspectionContextKey(contextKey) ?? getVehicleInspectionContextKey(nextInspectionDate);
  const matchingContexts = vehicleInspection ? contexts.filter((c) => c.contextKey === selectedContextKey) : [];
  const selectedContext = isGuEligible
    ? (matchingContexts.find((c) => c.checkVehicleAge === true) ??
      matchingContexts.find((c) => c.checkVehicleAge !== true) ??
      matchingContexts[0])
    : (matchingContexts.find((c) => c.checkVehicleAge !== true) ?? matchingContexts[0]);

  const urgencyLevel = getUrgencyLevel(nextInspectionDate);
  const daysUntil = nextInspectionDate ? getDaysUntilDate(new Date(nextInspectionDate)) : null;
  const isExpired = daysUntil !== null && daysUntil < 0;
  const showUrgencyBadge = vehicleInspection && urgencyLevel !== 'none';

  const appBannerContent = getAppBannerContent(selectedContextKey);

  const hasContextContent = Boolean(selectedContext);
  const hasModuleContent = false;
  const shouldShowBanner = Boolean(vehicleInspection && !errorSearch);
  const showAppBannerInColumn = shouldShowBanner && shouldShowAppBanner(hasContextContent, hasModuleContent);
  const showAppBannerAtBottom = shouldShowBanner && !shouldShowAppBanner(hasContextContent, hasModuleContent);

  const handleLookup = (licensePlateNumber: string) => {
    lookup({ licensePlateNumber });
  };

  return (
    <S.VehicleInspectionWrapper>
      <S.VehicleInspectionContent>
        <S.PvcTest>
          {showUrgencyBadge && (
            <UrgencyBadge urgencyLevel={urgencyLevel} deadline={createDate(nextInspectionDate)} isExpired={isExpired} />
          )}
          <SearchForm isLoading={isLoadingSearch} hasError={Boolean(errorSearch)} onLookup={handleLookup} />
          {!isLoadingSearch && !errorSearch && vehicleInspection && (
            <LookupResult vehicleInspection={vehicleInspection} selectedContextKey={selectedContextKey} />
          )}
        </S.PvcTest>
        {selectedContext && <ContextPanel context={selectedContext} badgeText={vehicleBadgeText} />}
        {showAppBannerInColumn && (
          <AppBanner
            title={appBannerContent.title}
            subtitle={appBannerContent.subtitle}
            iconText={appBannerContent.iconText}
            iconTextTwo={appBannerContent.iconTextTwo}
          />
        )}
      </S.VehicleInspectionContent>
      {showAppBannerAtBottom && (
        <AppBanner
          layout="full"
          title={appBannerContent.title}
          subtitle={appBannerContent.subtitle}
          iconText={appBannerContent.iconText}
          iconTextTwo={appBannerContent.iconTextTwo}
        />
      )}
      <S.InfoTextWrapper>
        <InfoText />
      </S.InfoTextWrapper>
      {/* We'll hide this for now until decided if we need these or not. */}
      {/* <OtherServices /> */}
    </S.VehicleInspectionWrapper>
  );
};
